import {
  Datagrid,
  List,
  Create,
  Edit,
  SimpleForm,
  TextField,
  TextInput,
  SelectInput,
  SelectField,
  Show,
  SimpleShowLayout,
  FunctionField,
  usePermissions,
} from 'react-admin';
import { Box } from '@mui/material';
import { ArchivalToolbar } from '../components/ArchivalToolbar';
import { ArchivedSelect, CoursesField, CoursesSelect } from '../components/selects';
import { hasRole, type Permissions, type User } from '../types';

const ROLES = [
  { id: 'instructor', name: 'Instructor' },
  { id: 'director', name: 'Course Director' },
  { id: 'admin', name: 'Admin' },
  { id: 'student', name: 'Student' },
];

const UserList = () => {
  const { permissions } = usePermissions<Permissions>();
  return (
    <List
      exporter={false}
      sort={{ field: 'email', order: 'ASC' }}
      filters={[<ArchivedSelect key="archived" />]}
      perPage={25}
    >
      <Datagrid
        rowClick={hasRole(permissions, 'tenant_admin') ? 'edit' : 'show'}
      >
        <TextField source="email" />
        <TextField source="first_name" />
        <TextField source="last_name" />
        <TextField source="type" />
      </Datagrid>
    </List>
  );
};

const UserShow = () => (
  <Show>
    <SimpleShowLayout>
      <FunctionField
        label="Name"
        render={(record: User) =>
          record
            ? `${record.first_name} ${record.middle_initial || 'X'} ${record.last_name}`
            : ''
        }
      />
      <TextField source="email" />
      <SelectField source="type" choices={ROLES} />
      <CoursesField />
    </SimpleShowLayout>
  </Show>
);

const UserCreate = () => (
  <Create>
    <UserForm />
  </Create>
);

const UserEdit = () => (
  <Edit mutationMode="pessimistic">
    <UserForm />
  </Edit>
);

const UserForm = () => {
  const { permissions } = usePermissions<Permissions>();
  const isAdmin = hasRole(permissions, 'tenant_admin');

  return (
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <TextInput disabled={!isAdmin} fullWidth source="email" />
      <TextInput disabled={!isAdmin} fullWidth source="sk" label="User ID" />
      <Box display="flex" gap={2} width="100%">
        <TextInput disabled={!isAdmin} source="first_name" />
        <TextInput disabled={!isAdmin} source="middle_initial" />
        <TextInput disabled={!isAdmin} source="last_name" />
      </Box>
      <SelectInput
        fullWidth
        disabled={!isAdmin}
        defaultValue="instructor"
        source="type"
        choices={ROLES}
      />
      <TextInput
        fullWidth
        disabled={!isAdmin}
        source="student_id"
        label="Student ID (for student accounts)"
      />
      <CoursesSelect />
    </SimpleForm>
  );
};

export const users = {
  list: UserList,
  show: UserShow,
  create: UserCreate,
  edit: UserEdit,
};
